import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Box, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { IoIosArrowBack } from "react-icons/io";
import {
  IoInformationCircleOutline,
  IoWarningOutline,
  IoCheckmarkCircleOutline,
} from "react-icons/io5";
import {
  useFetchProjectResourcesMutation,
  useFetchInvoiceProjectsQuery,
  useAllocateFixedCostInvoiceMutation,
  useFetchPreviousInvoicesMutation,
  useLazyFetchInvoiceByIdQuery,
} from "../../../slice/logdetailSlice";
import { CustomNoRowsOverlay } from "../../../components/common/OverlayGrid";
import { useTheme } from "../../../ThemeContext";
import { formatApiErrorMessage } from "../../../utils/formatApiErrorMessage";

/**
 * Custom Input for Adjusted Share with cursor preservation & arrow key steppers.
 * Prevents DataGrid from stealing arrow keys, preserves cursor location during typing,
 * and allows incrementing/decrementing via ArrowUp/ArrowDown or mini buttons.
 */
const AdjustedShareInput = ({ value, onChange, isDarkMode }) => {
  const inputRef = useRef(null);
  const cursorRef = useRef(null);

  // Restore cursor position on render if active
  useLayoutEffect(() => {
    if (
      inputRef.current &&
      document.activeElement === inputRef.current &&
      cursorRef.current !== null
    ) {
      const pos = Math.min(cursorRef.current, String(value ?? "").length);
      inputRef.current.setSelectionRange(pos, pos);
    }
  }, [value]);

  const handleInputChange = (e) => {
    const rawVal = e.target.value;
    // Allow empty string, or positive numbers up to 100 with up to 2 decimal places
    if (rawVal === "" || /^\d*\.?\d{0,2}$/.test(rawVal)) {
      const num = parseFloat(rawVal);
      if (rawVal === "" || isNaN(num) || num <= 100) {
        cursorRef.current = e.target.selectionStart;
        onChange(rawVal);
      }
    }
  };

  const handleStep = (step) => {
    const currentNum = parseFloat(value) || 0;
    const nextNum = Math.min(100, Math.max(0, Math.round((currentNum + step) * 100) / 100));
    cursorRef.current = null;
    onChange(String(nextNum));
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    // Stop DataGrid from intercepting arrow keys or home/end
    e.stopPropagation();

    if (e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.shiftKey ? 5 : 1;
      handleStep(step);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const step = e.shiftKey ? 5 : 1;
      handleStep(-step);
    }
  };

  return (
    <div className="d-flex align-items-center justify-content-end gap-1 w-100">
      <div
        className="d-inline-flex align-items-center rounded"
        style={{
          border: isDarkMode ? "1px solid #475569" : "1px solid #cbd5e1",
          backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
          padding: "2px 4px",
          transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className="text-end border-0 shadow-none p-0"
          style={{
            width: "52px",
            height: "22px",
            fontSize: "13px",
            fontFamily: "inherit",
            fontVariantNumeric: "tabular-nums",
            backgroundColor: isDarkMode ? "#1e293b" : "#ffffff",
            color: isDarkMode ? "#f8fafc" : "#0f172a",
            outline: "none",
          }}
          value={value ?? ""}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={(e) => {
            e.stopPropagation();
            cursorRef.current = e.target.selectionStart;
          }}
          onSelect={(e) => {
            cursorRef.current = e.target.selectionStart;
          }}
          onFocus={(e) => {
            e.target.style.backgroundColor = isDarkMode ? "#1e293b" : "#ffffff";
            if (e.target.parentElement) {
              e.target.parentElement.style.borderColor = isDarkMode ? "#38bdf8" : "#2563eb";
              e.target.parentElement.style.boxShadow = isDarkMode
                ? "0 0 0 2px rgba(56, 189, 248, 0.2)"
                : "0 0 0 2px rgba(37, 99, 235, 0.2)";
              e.target.parentElement.style.backgroundColor = isDarkMode ? "#1e293b" : "#ffffff";
            }
          }}
          onBlur={(e) => {
            e.target.style.backgroundColor = isDarkMode ? "#1e293b" : "#ffffff";
            if (e.target.parentElement) {
              e.target.parentElement.style.borderColor = isDarkMode ? "#475569" : "#cbd5e1";
              e.target.parentElement.style.boxShadow = "none";
              e.target.parentElement.style.backgroundColor = isDarkMode ? "#1e293b" : "#ffffff";
            }
          }}
        />

        {/* Mini Stepper Buttons */}
        <div
          className="d-flex flex-column ms-1 border-start ps-1"
          style={{
            borderColor: isDarkMode ? "#475569" : "#e2e8f0",
            lineHeight: 1,
            userSelect: "none",
          }}
        >
          <button
            type="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              handleStep(1);
            }}
            title="Increase (+1%)"
            className="btn btn-link p-0 text-decoration-none d-flex align-items-center justify-content-center"
            style={{
              width: "12px",
              height: "10px",
              fontSize: "8px",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              border: "none",
              background: "none",
              cursor: "pointer",
            }}
          >
            ▲
          </button>
          <button
            type="button"
            tabIndex={-1}
            onClick={(e) => {
              e.stopPropagation();
              handleStep(-1);
            }}
            title="Decrease (-1%)"
            className="btn btn-link p-0 text-decoration-none d-flex align-items-center justify-content-center"
            style={{
              width: "12px",
              height: "10px",
              fontSize: "8px",
              color: isDarkMode ? "#94a3b8" : "#64748b",
              border: "none",
              background: "none",
              cursor: "pointer",
            }}
          >
            ▼
          </button>
        </div>
      </div>
      <span style={{ fontSize: "12px", color: isDarkMode ? "#94a3b8" : "#64748b" }}>%</span>
    </div>
  );
};

/**
 * Screen 3b: Fixed Cost — Final Allocation Split Modal / Dedicated Inline Page
 * Built to specifications from Mockup Screen 3b & HTML 1 & 2.
 * Pools receipts for a scope/project and executes final revenue split proportional to resource cost.
 * Integrated with POST /invoices/project-resources API.
 */
const FinalAllocationModal = ({
  show,
  onHide,
  invoice,
  onConfirmAllocation,
  onBackToScope,
  isInline = false,
}) => {
  const { isDarkMode } = useTheme();
  const { data: projectsResponse } = useFetchInvoiceProjectsQuery();
  const projectList = useMemo(() => projectsResponse?.data || [], [projectsResponse]);

  const [fetchProjectResources, { isLoading }] = useFetchProjectResourcesMutation();
  const [allocateFixedCostInvoice, { isLoading: isAllocatingFixedCost }] =
    useAllocateFixedCostInvoiceMutation();
  const [fetchPreviousInvoices] = useFetchPreviousInvoicesMutation();
  const [fetchInvoiceById] = useLazyFetchInvoiceByIdQuery();

  const [splitRows, setSplitRows] = useState([]);
  const [initialRows, setInitialRows] = useState([]);
  const [apiSummary, setApiSummary] = useState(null);
  const [prevInvoicesSummary, setPrevInvoicesSummary] = useState(
    invoice?.prev_invoices_summary || null
  );
  const [prevInvoicesCount, setPrevInvoicesCount] = useState(
    invoice?.receipts_count || null
  );
  const [prevInvoices, setPrevInvoices] = useState(
    invoice?.prev_invoices || []
  );
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");

  useEffect(() => {
    if (invoice?.prev_invoices_summary) {
      setPrevInvoicesSummary(invoice.prev_invoices_summary);
    }
    if (invoice?.receipts_count != null) {
      setPrevInvoicesCount(invoice.receipts_count);
    }
    if (Array.isArray(invoice?.prev_invoices)) {
      setPrevInvoices(invoice.prev_invoices);
    }
  }, [invoice]);

  const isAllocated = useMemo(() => {
    const st = String(invoice?.status || invoice?.rawItem?.status || "").toUpperCase();
    return st === "ALLOCATED" || Boolean(invoice?.is_allocated || invoice?.rawItem?.is_allocated);
  }, [invoice]);

  // Resolve project_id from invoice or projectList
  const resolvedProjectId = useMemo(() => {
    if (invoice?.project_id) return Number(invoice.project_id);
    if (invoice?.rawItem?.project_id) return Number(invoice.rawItem.project_id);
    const title = invoice?.project || invoice?.project_name;
    if (title && Array.isArray(projectList)) {
      const matched = projectList.find(
        (p) => p.title === title || p.project_name === title || p.name === title
      );
      if (matched?.project_id) return Number(matched.project_id);
      if (matched?.id) return Number(matched.id);
    }
    return null;
  }, [invoice, projectList]);

  const totalReceived = useMemo(() => {
    if (invoice?.total_received != null && Number(invoice.total_received) > 0) {
      return Number(invoice.total_received);
    }
    if (invoice?.received_amount != null && Number(invoice.received_amount) > 0) {
      return Number(invoice.received_amount);
    }
    if (Array.isArray(prevInvoices) && prevInvoices.length > 0) {
      const currentInvId = Number(invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id || 0);
      const currentInvoiceNumber = invoice?.invoice || invoice?.invoice_number;
      const currentNumAmt = Number(String(invoice?.total_amount || invoice?.amount || invoice?.rawItem?.total_amount || 0).replace(/,/g, "")) || 0;
      const alreadyIncludes = prevInvoices.some(
        (r) => (currentInvId && (r.invoice_id === currentInvId || r.id === currentInvId)) ||
               (currentInvoiceNumber && (r.invoice_number === currentInvoiceNumber || r.invoice === currentInvoiceNumber))
      );
      const prevSum = prevInvoices.reduce((sum, inv) => {
        const rawAmt = inv.total_amount ?? inv.amount ?? 0;
        return sum + (Number(String(rawAmt).replace(/,/g, "")) || 0);
      }, 0);
      const total = prevSum + (alreadyIncludes ? 0 : currentNumAmt);
      if (total > 0) return total;
    }
    if (invoice?.amount) {
      const num = Number(String(invoice.amount).replace(/,/g, ""));
      if (!isNaN(num) && num > 0) return num;
    }
    if (invoice?.total_amount) {
      const num = Number(String(invoice.total_amount).replace(/,/g, ""));
      if (!isNaN(num) && num > 0) return num;
    }
    if (apiSummary?.received_amount != null && Number(apiSummary.received_amount) > 0) {
      return Number(apiSummary.received_amount);
    }
    return 0;
  }, [invoice, prevInvoices, apiSummary]);

  const contractBudget = useMemo(() => {
    if (invoice?.contract_budget != null && Number(invoice.contract_budget) > 0) {
      return Number(invoice.contract_budget);
    }
    if (prevInvoicesSummary?.budget_amount != null && Number(prevInvoicesSummary.budget_amount) > 0) {
      return Number(prevInvoicesSummary.budget_amount);
    }
    if (invoice?.project?.total_budget != null && Number(invoice.project.total_budget) > 0) {
      return Number(invoice.project.total_budget);
    }
    if (invoice?.rawItem?.project?.total_budget != null && Number(invoice.rawItem.project.total_budget) > 0) {
      return Number(invoice.rawItem.project.total_budget);
    }
    const matched = Array.isArray(projectList)
      ? projectList.find(
          (p) => String(p.project_id) === String(resolvedProjectId) || String(p.id) === String(resolvedProjectId)
        )
      : null;
    return Number(matched?.total_budget || matched?.budget || matched?.contract_value || matched?.budget_amount || 0);
  }, [invoice, prevInvoicesSummary, projectList, resolvedProjectId]);

  const pendingAmount = useMemo(() => {
    if (invoice?.pending_amount != null) {
      return Number(invoice.pending_amount);
    }
    if (prevInvoicesSummary?.pending_amount != null) {
      return Number(prevInvoicesSummary.pending_amount);
    }
    if (contractBudget > 0) {
      return Math.max(0, contractBudget - totalReceived);
    }
    return 0;
  }, [invoice, prevInvoicesSummary, contractBudget, totalReceived]);

  const projectName = useMemo(() => {
    return (
      invoice?.project_name ||
      (typeof invoice?.project === "string" ? invoice.project : invoice?.project?.title) ||
      invoice?.rawItem?.project_name ||
      invoice?.rawItem?.project?.title ||
      (resolvedProjectId && Array.isArray(projectList)
        ? projectList.find(
            (p) => String(p.project_id) === String(resolvedProjectId) || String(p.id) === String(resolvedProjectId)
          )?.title || ""
        : "")
    );
  }, [invoice, resolvedProjectId, projectList]);

  // Helper to map resources list to DataGrid row items
  const mapResourcesToRows = useCallback(
    (list) => {
      if (!Array.isArray(list)) return [];
      return list.map((item, idx) => {
        const costNum = Number(item.cost || 0);
        const compShare = Number(item.computed_share ?? 0);
        const adjShare = item.adjusted_share != null ? String(item.adjusted_share) : String(compShare);
        const earningVal = item.earning != null
          ? Number(item.earning)
          : (totalReceived * ((parseFloat(adjShare) || 0) / 100));

        const hrs = parseFloat(item.working_hours != null ? item.working_hours : (item.hours != null ? item.hours : 0)) || 0;
        const resolvedRate =
          Number(item.rate_per_hours?.[0]?.per_hr_rate) ||
          Number(item.rate_per_hour) ||
          Number(item.rate) ||
          Number(item.per_hr_rate) ||
          (costNum && hrs ? Math.round(costNum / hrs) : 0);

        const marginVal = item.profit_margin != null
          ? Number(item.profit_margin)
          : (earningVal - costNum);

        return {
          id: item.user_id || item.invoice_resource_id || idx + 1,
          user_id: item.user_id,
          resource: item.name || item.user_name || item.resource || "Unknown",
          hours: item.working_hours != null ? String(item.working_hours) : (item.hours != null ? String(item.hours) : "0"),
          rate: resolvedRate,
          cost: costNum,
          computedPercent: `${compShare}%`,
          computedShareNum: compShare,
          adjustedPercent: adjShare,
          earning: earningVal.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          earningNum: earningVal,
          margin: marginVal.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }),
          marginNum: marginVal,
          profit_margin: marginVal,
        };
      });
    },
    [totalReceived]
  );

  // Fetch project or allocated invoice resources dynamically
  useEffect(() => {
    if (!show && !isInline) return;

    const invId = invoice?.id || invoice?.invoice_id || invoice?.rawItem?.id || invoice?.rawItem?.invoice_id;

    // Case 1: When invoice is ALREADY ALLOCATED (Read-only view)
    if (isAllocated) {
      // 1a. Load directly from invoice prop if present
      const directResources =
        invoice?.resources ||
        invoice?.rawItem?.resources ||
        invoice?.invoice_resources ||
        invoice?.rawItem?.invoice_resources;

      if (Array.isArray(directResources) && directResources.length > 0) {
        const mapped = mapResourcesToRows(directResources);
        setSplitRows(mapped);
        setInitialRows(mapped);
      }

      const directReason =
        invoice?.reason ||
        invoice?.rawItem?.reason ||
        invoice?.adjustment_reason ||
        invoice?.rawItem?.adjustment_reason ||
        invoice?.notes ||
        invoice?.rawItem?.notes;
      if (directReason) {
        setReason(directReason);
      }

      // 1b. Fetch full invoice details from backend GET /invoices/{id}
      if (invId) {
        fetchInvoiceById(Number(invId))
          .unwrap()
          .then((invRes) => {
            const invData = invRes?.data || invRes;
            if (invData?.reason || invData?.adjustment_reason || invData?.adjusted_reason || invData?.notes) {
              setReason(invData.reason || invData.adjustment_reason || invData.adjusted_reason || invData.notes || "");
            }
            const resList = invData?.resources || invData?.invoice_resources;
            if (Array.isArray(resList) && resList.length > 0) {
              const mapped = mapResourcesToRows(resList);
              setSplitRows(mapped);
              setInitialRows(mapped);
            }
          })
          .catch((err) => {
            console.error("fetchInvoiceById error:", err);
          });
      }


      return;
    }

    // Case 2: When invoice is NOT YET ALLOCATED (Fetch provisional resources from project logs)
    if (resolvedProjectId) {
      const recAmt =
        invoice?.total_received != null && Number(invoice.total_received) > 0
          ? Number(invoice.total_received)
          : invoice?.received_amount != null && Number(invoice.received_amount) > 0
          ? Number(invoice.received_amount)
          : invoice?.amount
          ? Number(String(invoice.amount).replace(/,/g, ""))
          : undefined;

      fetchProjectResources({
        project_id: resolvedProjectId,
        ...(recAmt ? { received_amount: String(recAmt) } : {}),
      })
        .unwrap()
        .then((res) => {
          if (res && res.status && Array.isArray(res.data)) {
            setApiSummary({
              received_amount: res.received_amount != null ? Number(res.received_amount) : recAmt || 0,
              total_cost: res.total_cost != null ? Number(res.total_cost) : 0,
              total_earning: res.total_earning != null ? Number(res.total_earning) : 0,
              total_profit_margin: res.total_profit_margin != null ? Number(res.total_profit_margin) : 0,
              reporting_month: res.reporting_month || "",
            });

            if (res.adjustment_reason || res.reason) {
              setReason(res.adjustment_reason || res.reason);
            } else if (
              invoice?.adjustment_reason ||
              invoice?.rawItem?.adjustment_reason ||
              invoice?.reason ||
              invoice?.rawItem?.reason
            ) {
              setReason(
                invoice?.adjustment_reason ||
                invoice?.rawItem?.adjustment_reason ||
                invoice?.reason ||
                invoice?.rawItem?.reason ||
                ""
              );
            }

            const mapped = mapResourcesToRows(res.data);
            setSplitRows(mapped);
            setInitialRows(mapped);
          } else {
            setSplitRows([]);
            setInitialRows([]);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch project resources:", err);
          setSplitRows([]);
          setInitialRows([]);
        });


    } else {
      setSplitRows([]);
      setInitialRows([]);
      setPrevInvoicesSummary(null);
      setPrevInvoicesCount(null);
    }
  }, [
    show,
    isInline,
    isAllocated,
    invoice,
    resolvedProjectId,
    fetchInvoiceById,
    fetchProjectResources,
    fetchPreviousInvoices,
    mapResourcesToRows,
  ]);

  // Unified effect to fetch previous invoices summary (runs once per modal open)
  useEffect(() => {
    if ((!show && !isInline) || !resolvedProjectId) return;
    fetchPreviousInvoices({ project_id: resolvedProjectId })
      .unwrap()
      .then((res) => {
        if (res?.summary) setPrevInvoicesSummary(res.summary);
        if (Array.isArray(res?.data)) {
          setPrevInvoices(res.data);
          setPrevInvoicesCount(res.data.length);
        }
      })
      .catch(() => {});
  }, [show, isInline, resolvedProjectId, fetchPreviousInvoices]);

  const handlePercentChange = useCallback(
    (id, newPercent) => {
      setSplitRows((prev) =>
        prev.map((row) => {
          if (row.id === id) {
            const percentVal = parseFloat(newPercent) || 0;
            const newEarningNum = totalReceived * (percentVal / 100);
            const newMarginNum = newEarningNum - (row.cost || 0);
            return {
              ...row,
              adjustedPercent: newPercent,
              earning: newEarningNum.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              earningNum: newEarningNum,
              margin: newMarginNum.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
              marginNum: newMarginNum,
              profit_margin: newMarginNum,
            };
          }
          return row;
        })
      );
    },
    [totalReceived]
  );

  const handleResetComputed = () => {
    if (initialRows.length > 0) {
      setSplitRows(
        initialRows.map((r) => {
          const compVal = Number(r.computedShareNum) || 0;
          const resetEarning = (totalReceived * (compVal / 100));
          return {
            ...r,
            adjustedPercent: String(compVal),
            earning: resetEarning.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
            earningNum: resetEarning,
          };
        })
      );
    }
    setReason("");
    setReasonError("");
    toast.info("Proportions reset to computed share.");
  };

  const handleConfirm = async () => {
    if (isAllocated) return;
    const invId =
      invoice?.id ||
      invoice?.invoice_id ||
      invoice?.rawItem?.id ||
      invoice?.rawItem?.invoice_id;

    if (!invId) {
      toast.error("Invoice ID is missing");
      return;
    }

    if (!splitRows || splitRows.length === 0) {
      toast.warn("No resource rows available to allocate.");
      return;
    }

    // 100% validation check per Mockup Screen 3b specifications
    const totalAdjustedNum = splitRows.reduce(
      (s, r) => s + (parseFloat(r.adjustedPercent) || 0),
      0
    );
    if (Math.abs(totalAdjustedNum - 100) > 0.01) {
      toast.warning(
        `Total shares must equal exactly 100%. Current total: ${totalAdjustedNum.toFixed(2)}%`
      );
      return;
    }

    const isAnyAdjusted = splitRows.some(
      (r) => Math.abs((parseFloat(r.adjustedPercent) || 0) - (r.computedShareNum || 0)) > 0.01
    );
    if (isAnyAdjusted && !reason.trim()) {
      setReasonError("Please provide a reason for adjusting resource shares.");
      const reasonInput = document.getElementById("re-allocation-reason-input");
      if (reasonInput) reasonInput.focus();
      return;
    }

    const confirmResult = await Swal.fire({
      title: isAllocated ? "Update Allocation?" : "Confirm Final Allocation?",
      html: `
        <div style="text-align: left; font-size: 13.5px; color: ${isDarkMode ? "#cbd5e1" : "#374151"};">
          <p style="margin-bottom: 8px;">Are you sure you want to ${isAllocated ? "update the allocation for" : "finalize and allocate earnings for"} <strong>${projectName || invoice?.project || invoice?.project_name || "this project"}</strong>?</p>
          <div style="background-color: ${isDarkMode ? "#1e293b" : "#f8fafc"}; padding: 10px 14px; border-radius: 6px; border: 1px solid ${isDarkMode ? "#334155" : "#e2e8f0"}; font-size: 12.5px; line-height: 1.6;">
            <div><strong>Project:</strong> ${projectName || invoice?.project || invoice?.project_name || "—"}</div>
            <div><strong>Total Budget:</strong> ₹${Number(contractBudget || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div><strong>Total Received Amount:</strong> ₹${Number(totalReceived || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div><strong>Pending:</strong> ₹${Number(pendingAmount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </div>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: isAllocated ? "Yes, Update Allocation" : "Yes, Confirm & Allocate",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#04a189",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      allowOutsideClick: false,
      background: isDarkMode ? "#0f172a" : "#fff",
      color: isDarkMode ? "#f8fafc" : "#1e293b",
    });

    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      const invoiceResources = splitRows.map((r) => {
        const earningVal = Number(
          r.earningNum !== undefined
            ? r.earningNum
            : String(r.earning || 0).replace(/,/g, "")
        );
        return {
          user_id: Number(r.user_id || r.id),
          adjusted_share: parseFloat(Number(r.adjustedPercent || 0).toFixed(2)),
          earning: parseFloat((isNaN(earningVal) ? 0 : earningVal).toFixed(2)),
        };
      });

      const payload = {
        invoice_id: Number(invId),
        invoice_resources: invoiceResources,
        ...(reason.trim() ? { adjustment_reason: reason.trim() } : {}),
      };

      const res = await allocateFixedCostInvoice(payload).unwrap();

      if (res && res.status) {
        toast.success(res.message || "Fixed cost invoice allocated successfully");
        if (onConfirmAllocation) {
          onConfirmAllocation();
        }
        onHide();
      } else {
        toast.error(res?.message || "Failed to allocate fixed cost invoice");
      }
    } catch (err) {
      console.error("Failed to allocate fixed cost invoice:", err);
      toast.error(formatApiErrorMessage(err, "Failed to allocate fixed cost invoice"));
    }
  };

  const totalHours = useMemo(() => {
    return splitRows.reduce((s, r) => s + (parseFloat(r.hours) || 0), 0);
  }, [splitRows]);

  const totalCost = useMemo(() => {
    if (apiSummary?.total_cost != null) return apiSummary.total_cost;
    return splitRows.reduce((s, r) => s + (Number(r.cost) || 0), 0);
  }, [apiSummary, splitRows]);

  const totalAdjusted = useMemo(
    () => splitRows.reduce((s, r) => s + (parseFloat(r.adjustedPercent) || 0), 0),
    [splitRows]
  );

  const isAnyAdjusted = useMemo(() => {
    return splitRows.some(
      (r) => Math.abs((parseFloat(r.adjustedPercent) || 0) - (r.computedShareNum || 0)) > 0.01
    );
  }, [splitRows]);

  const totalEarningSum = useMemo(() => {
    return splitRows.reduce((s, r) => s + (Number(r.earningNum) || 0), 0);
  }, [splitRows]);

  const projectMargin = totalCost > 0 ? (totalReceived / totalCost).toFixed(2) : "0.00";
  const totalMargin = useMemo(() => {
    return totalReceived - totalCost;
  }, [totalReceived, totalCost]);
  const receiptsPooled = useMemo(() => {
    if (invoice?.receipts_count != null) return invoice.receipts_count;
    if (prevInvoicesCount != null) return prevInvoicesCount;
    if (Array.isArray(prevInvoices) && prevInvoices.length > 0) {
      const currentInvId = Number(invoice?.rawItem?.invoice_id || invoice?.id || invoice?.invoice_id || 0);
      const currentInvoiceNumber = invoice?.invoice || invoice?.invoice_number;
      const alreadyIncludes = prevInvoices.some(
        (r) => (currentInvId && (r.invoice_id === currentInvId || r.id === currentInvId)) ||
               (currentInvoiceNumber && (r.invoice_number === currentInvoiceNumber || r.invoice === currentInvoiceNumber))
      );
      return prevInvoices.length + (alreadyIncludes ? 0 : 1);
    }
    return 1;
  }, [invoice, prevInvoicesCount, prevInvoices]);

  const formattedTotalAmount = useMemo(() => {
    const amt =
      totalReceived > 0
        ? totalReceived
        : Number(String(invoice?.total_amount || invoice?.amount || invoice?.rawItem?.total_amount || 0).replace(/,/g, "")) || 0;
    return amt.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, [totalReceived, invoice]);

  const columns = useMemo(
    () => [
      {
        field: "resource",
        headerName: "Resource",
        flex: 1.4,
        minWidth: 160,
        renderCell: (params) => (
          <span className="fw-medium" style={{ fontSize: "13px" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "hours",
        headerName: "Hours",
        width: 100,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span style={{ fontSize: "13px", fontVariantNumeric: "tabular-nums" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "rate",
        headerName: "Rate",
        width: 110,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span style={{ fontSize: "13px", fontVariantNumeric: "tabular-nums" }}>
            ₹{Number(params.value || 0).toLocaleString("en-IN")}
          </span>
        ),
      },
      {
        field: "cost",
        headerName: "Cost",
        width: 130,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span style={{ fontSize: "13px", fontVariantNumeric: "tabular-nums" }}>
            ₹{Number(params.value || 0).toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        ),
      },
      {
        field: "computedPercent",
        headerName: "Computed share",
        width: 140,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span className="text-muted" style={{ fontSize: "13px", fontVariantNumeric: "tabular-nums" }}>
            {params.value}
          </span>
        ),
      },
      {
        field: "adjustedPercent",
        headerName: "Adjusted share",
        width: 150,
        headerAlign: "right",
        align: "right",
        renderCell: (params) =>
          isAllocated ? (
            <span
              style={{
                fontSize: "13px",
                fontVariantNumeric: "tabular-nums",
                color: isDarkMode ? "#cbd5e1" : "#374151",
              }}
            >
              {params.value != null && params.value !== ""
                ? `${Number(params.value).toFixed(2)}%`
                : "-"}
            </span>
          ) : (
            <AdjustedShareInput
              value={params.value}
              onChange={(val) => handlePercentChange(params.row.id, val)}
              isDarkMode={isDarkMode}
            />
          ),
      },
      {
        field: "earning",
        headerName: "Earning",
        width: 150,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span
            className="fw-semibold"
            style={{
              color: isDarkMode ? "#4ade80" : "#16a34a",
              fontSize: "13px",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            ₹{params.value}
          </span>
        ),
      },
      {
        field: "margin",
        headerName: "Margin",
        width: 150,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => {
          const num = Number(params.row.marginNum ?? String(params.value || 0).replace(/,/g, ""));
          const isPositive = num >= 0;
          return (
            <span
              className="fw-semibold"
              style={{
                color: isPositive ? (isDarkMode ? "#38bdf8" : "#0284c7") : "#ef4444",
                fontSize: "13px",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              ₹{params.value}
            </span>
          );
        },
      },
    ],
    [isDarkMode, handlePercentChange, isAllocated]
  );

  const renderHeader = () => (
    <div className="d-flex align-items-center justify-content-between w-100 pe-2">
      <div className="d-flex align-items-center gap-2">
        {isInline && (
          <button
            type="button"
            className="R_btn me-2"
            onClick={onBackToScope || onHide}
            title="Back to Fixed Cost Mapping"
            style={{
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              border: isDarkMode ? "1px solid #444" : "1px solid #e2e8f0",
              backgroundColor: isDarkMode ? "#2a2a2a" : "#fff",
              color: isDarkMode ? "#cbd5e1" : "#475569",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? "#333333" : "#f1f5f9";
              e.currentTarget.style.borderColor = isDarkMode ? "#555555" : "#cbd5e1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = isDarkMode ? "#2a2a2a" : "#fff";
              e.currentTarget.style.borderColor = isDarkMode ? "#444" : "#e2e8f0";
            }}
          >
            <IoIosArrowBack size={20} />
          </button>
        )}
        <span className="fs-6 fw-bold m-0">
          Final Allocation{projectName ? ` · ${projectName}` : ""}{formattedTotalAmount ? ` · ₹${formattedTotalAmount}` : ""}
        </span>
        {invoice?.source === "MANUAL" || invoice?.source === "MANINV" || invoice?.source === "CASH" ? (
          <span className="badge-source-manual">MANUAL</span>
        ) : (
          <span className="badge bg-light text-dark border">
            {invoice?.source || "TT"}
          </span>
        )}
        <span className="badge-model-fixed">
          Fixed Cost
        </span>
        {isAllocated ? (
          <span className="badge-status-allocated">Allocated</span>
        ) : pendingAmount > 0 ? (
          <span className="badge-status-mapped">Pending Receipts</span>
        ) : (
          <span className="badge-status-allocated">Ready to Allocate</span>
        )}
      </div>

      {/* Header Tooltip Icons: Info, Warning, and Tick placed before the X close button */}
      <div className="d-flex align-items-center gap-2 me-1">
        <span
          className="badge px-2.5 py-1"
          style={{
            fontSize: "12px",
            fontWeight: 500,
            backgroundColor: isDarkMode ? "#1e293b" : "#f1f5f9",
            color: isDarkMode ? "#cbd5e1" : "#334155",
            border: isDarkMode ? "1px solid #334155" : "1px solid #cbd5e1",
            borderRadius: "6px",
          }}
        >
          Receipts Pooled: <strong className="ms-1">{receiptsPooled}</strong>
        </span>

        <Tooltip
          title={
            <div style={{ fontSize: "12px", lineHeight: "1.5", maxWidth: "320px", padding: "2px" }}>
              <strong>Receipts Pooled:</strong> Pooling {receiptsPooled} receipts totalling ₹{totalReceived.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} across {totalHours.toFixed(2)} hours logged by {splitRows.length} resources over the life of the project. When each payment arrived makes no difference here — only the total matters.
            </div>
          }
          arrow
          placement="bottom"
        >
          <span
            style={{
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: isDarkMode ? "rgba(14, 165, 233, 0.15)" : "#eff6ff",
              color: isDarkMode ? "#38bdf8" : "#0284c7",
              border: isDarkMode ? "1px solid rgba(14, 165, 233, 0.3)" : "1px solid #bfdbfe",
              transition: "all 0.2s ease",
            }}
          >
            <IoInformationCircleOutline size={17} />
          </span>
        </Tooltip>

        <Tooltip
          title={
            <div style={{ fontSize: "12px", lineHeight: "1.5", maxWidth: "330px", padding: "2px" }}>
              <strong>Override Guide:</strong> Adjust where hours misrepresent contribution. If one resource burned time through rework while another delivered quickly in fewer hours, override the share here. A reason is required and the rows are flagged as manual override. The total is fixed at ₹{totalReceived.toLocaleString("en-IN")} — what one gains, another loses.
            </div>
          }
          arrow
          placement="bottom"
        >
          <span
            style={{
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: isDarkMode ? "rgba(234, 179, 8, 0.15)" : "#fffbeb",
              color: isDarkMode ? "#facc15" : "#d97706",
              border: isDarkMode ? "1px solid rgba(234, 179, 8, 0.3)" : "1px solid #fde68a",
              transition: "all 0.2s ease",
            }}
          >
            <IoWarningOutline size={17} />
          </span>
        </Tooltip>

        <Tooltip
          title={
            <div style={{ fontSize: "12px", lineHeight: "1.5", maxWidth: "300px", padding: "2px" }}>
              <strong>Allocation Status:</strong> {isAllocated ? "This project has already been allocated." : "Confirming sets both receipts to Allocated and writes the earnings against the month of project completion."}
            </div>
          }
          arrow
          placement="bottom"
        >
          <span
            style={{
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "28px",
              height: "28px",
              borderRadius: "50%",
              backgroundColor: isDarkMode ? "rgba(16, 185, 129, 0.15)" : "#ecfdf5",
              color: isDarkMode ? "#34d399" : "#059669",
              border: isDarkMode ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid #a7f3d0",
              transition: "all 0.2s ease",
            }}
          >
            <IoCheckmarkCircleOutline size={17} />
          </span>
        </Tooltip>
      </div>
    </div>
  );

  const renderBody = () => (
    <>
      {/* KPI Cards from Mockup Screen 3b */}
      <div className="row g-2 mb-3">
        <div className="col">
          <div className={`p-3 border rounded text-center h-100 shadow-sm ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
            <div className="text-muted small fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
              TOTAL BUDGET
            </div>
            <div className="fw-bold mt-1" style={{ fontSize: "18px" }}>
              ₹{Number(contractBudget || 0).toLocaleString("en-IN")}
            </div>
          </div>
        </div>
        <div className="col">
          <div className={`p-3 border rounded text-center h-100 shadow-sm ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
            <div className="text-muted small fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
              TOTAL RECEIVED
            </div>
            <div className="fw-bold mt-1 text-success" style={{ fontSize: "18px" }}>
              ₹{totalReceived.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <div className="col">
          <div className={`p-3 border rounded text-center h-100 shadow-sm ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
            <div className="text-muted small fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
              PENDING AMOUNT
            </div>
            <div className={`fw-bold mt-1 ${pendingAmount > 0 ? "text-warning" : ""}`} style={{ fontSize: "18px" }}>
              ₹{pendingAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <div className="col">
          <div className={`p-3 border rounded text-center h-100 shadow-sm ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
            <div className="text-muted small fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
              TOTAL COST
            </div>
            <div className="fw-bold mt-1" style={{ fontSize: "18px" }}>
              ₹{Number(totalCost).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
          </div>
        </div>
        <div className="col">
          <div className={`p-3 border rounded text-center h-100 shadow-sm ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
            <div className="text-muted small fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
              TOTAL HOURS
            </div>
            <div className="fw-bold mt-1" style={{ fontSize: "18px" }}>
              {totalHours.toFixed(2)}
            </div>
          </div>
        </div>
        <div className="col">
          <div className={`p-3 border rounded text-center h-100 shadow-sm ${isDarkMode ? "bg-dark border-secondary text-white" : "bg-light"}`}>
            <div className="text-muted small fw-semibold" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
              PROJECT MARGIN
            </div>
            <div className="fw-bold mt-1 text-success" style={{ fontSize: "18px" }}>
              {projectMargin}x
            </div>
          </div>
        </div>
      </div>



      {/* Section Header: Final split */}
      <div className="d-flex align-items-center mb-2">
        <div className="d-flex align-items-center gap-2">
          <span
            style={{
              width: "9px",
              height: "9px",
              borderRadius: "50%",
              backgroundColor: "#10b981",
              display: "inline-block",
            }}
          />
          <span className="fw-semibold" style={{ fontSize: "14px" }}>
            Final split
          </span>
        </div>
      </div>

      {/* Final Split MUI DataGrid Table */}
      <Box
        sx={{
          width: "100%",
          mb: 3,
          backgroundColor: isDarkMode ? "transparent" : "#fff",
          border: isDarkMode ? "1px solid #444" : "1px solid #e0e0e0",
          borderRadius: "6px",
          overflow: "hidden",
        }}
      >
        <DataGrid
          className="final-allocation-datagrid"
          rows={splitRows}
          columns={columns}
          autoHeight={splitRows.length <= 10}
          columnHeaderHeight={52}
          rowHeight={52}
          loading={isLoading}
          hideFooterPagination
          disableRowSelectionOnClick
          disableColumnMenu
          getRowId={(row) => row.id}
          slots={{
            footer: () => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderTop: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
                  backgroundColor: isDarkMode ? "#1e1e1e" : "#f8fafc",
                  minHeight: "50px",
                  px: 0,
                  width: "100%",
                  fontWeight: 700,
                  fontSize: "13px",
                  color: isDarkMode ? "#f2f1f1" : "#1e293b",
                  pr: 0,
                }}
              >
                <Box
                  sx={{
                    flex: 1.4,
                    minWidth: 160,
                    fontWeight: 700,
                    fontSize: "13px",
                    color: isDarkMode ? "#f2f1f1" : "#1e293b",
                    pl: "16px",
                  }}
                >
                  Total
                </Box>
                <Box
                  sx={{
                    width: 100,
                    textAlign: "right",
                    pr: "10px",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {totalHours.toFixed(2)}
                </Box>
                <Box sx={{ width: 110 }} />
                <Box
                  sx={{
                    width: 130,
                    textAlign: "right",
                    pr: "10px",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ₹{Number(totalCost).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Box>
                <Box
                  sx={{
                    width: 140,
                    textAlign: "right",
                    pr: "10px",
                    color: isDarkMode ? "#94a3b8" : "#475569",
                  }}
                >
                  100%
                </Box>
                <Box
                  sx={{
                    width: 150,
                    textAlign: "right",
                    pr: "14px",
                    color: isDarkMode ? "#4ade80" : "#16a34a",
                  }}
                >
                  {totalAdjusted.toFixed(2)}% {Math.abs(totalAdjusted - 100) < 0.01 ? "✓" : ""}
                </Box>
                <Box
                  sx={{
                    width: 150,
                    textAlign: "right",
                    pr: "14px",
                    color: isDarkMode ? "#4ade80" : "#16a34a",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ₹{totalReceived.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Box>
                <Box
                  sx={{
                    width: 150,
                    textAlign: "right",
                    pr: "16px",
                    color: isDarkMode ? "#38bdf8" : "#0284c7",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  ₹{Number(totalMargin).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Box>
              </Box>
            ),
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          sx={{
            height: splitRows.length > 10 ? 624 : "auto",
            border: "none !important",
            borderRadius: 0,
            "& .MuiDataGrid-scrollbar, & .MuiDataGrid-scrollbar--vertical": {
              opacity: 0,
              transition: "opacity 0.2s ease-in-out",
              scrollbarWidth: "thin",
              scrollbarColor: "#cbd5e1 transparent",
              "&::-webkit-scrollbar": {
                width: "4px",
                height: "4px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#cbd5e1",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#94a3b8",
              },
              "&::-webkit-scrollbar-button": {
                display: "none",
                width: 0,
                height: 0,
              },
            },
            "&:hover .MuiDataGrid-scrollbar, &:hover .MuiDataGrid-scrollbar--vertical": {
              opacity: 1,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
              borderBottom: isDarkMode ? "1px solid #333" : "1px solid #e0e0e0",
              fontSize: "13px",
              fontWeight: 600,
              color: isDarkMode ? "#f2f1f1" : "#6b7280",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-cell": {
              fontSize: "13px",
              borderBottom: isDarkMode ? "1px solid #2a2a2a" : "1px solid #f0f0f0",
              display: "flex",
              alignItems: "center",
              overflow: "visible !important",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fafafa",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e0e0e0",
              minHeight: "50px",
            },
          }}
        />
      </Box>



      {/* Reason Input Field from Mockup Screen 3b */}
      <div className="mb-3">
        <label className="form-label small fw-semibold text-muted mb-1" style={{ fontSize: "11px", letterSpacing: "0.2px" }}>
          REASON (REQUIRED IF ADJUSTED)
          {!isAllocated && isAnyAdjusted && <span style={{ color: "#dc2626", marginLeft: "4px" }}>*</span>}
        </label>
        <input
          id="re-allocation-reason-input"
          type="text"
          className={`form-control shadow-none ${!isAllocated ? "re-focus-gray" : ""} ${
            isDarkMode ? "bg-dark text-white" : "bg-white text-dark"
          }`}
          style={{
            fontSize: "13px",
            borderColor: !isAllocated && reasonError ? "#dc2626" : (isDarkMode ? "#475569" : "#cbd5e1"),
            backgroundColor: isAllocated ? (isDarkMode ? "#1e293b" : "#f1f5f9") : undefined,
            cursor: isAllocated ? "default" : "text",
            transition: "border-color 0.15s ease-in-out",
          }}
          disabled={isAllocated}
          readOnly={isAllocated}
          placeholder={
            isAllocated
              ? reason ? "" : "No adjustment reason provided"
              : "Enter reason for share adjustment (e.g., rework, delivery speed, scope change)..."
          }
          value={reason}
          onChange={(e) => {
            if (isAllocated) return;
            setReason(e.target.value);
            if (reasonError && e.target.value.trim()) {
              setReasonError("");
            }
          }}
          onFocus={(e) => {
            if (!isAllocated && !reasonError) {
              e.target.style.setProperty("border-color", isDarkMode ? "#cbd5e1" : "#475569", "important");
            }
          }}
          onBlur={(e) => {
            if (!isAllocated && !reasonError) {
              e.target.style.removeProperty("border-color");
              e.target.style.borderColor = isDarkMode ? "#475569" : "#cbd5e1";
            }
          }}
        />
        {!isAllocated && reasonError && (
          <div className="text-danger mt-1" style={{ fontSize: "12px", fontWeight: 500 }}>
            {reasonError}
          </div>
        )}
      </div>

      {!isAllocated && pendingAmount > 0 && (
        <div
          className="py-2 px-3 mb-2 d-flex align-items-center"
          style={{
            fontSize: "12px",
            borderRadius: "6px",
            backgroundColor: isDarkMode ? "rgba(234, 179, 8, 0.12)" : "#fffbeb",
            color: isDarkMode ? "#fde047" : "#854d0e",
            border: isDarkMode ? "1px solid rgba(234, 179, 8, 0.35)" : "1px solid #fef08a",
          }}
        >
          <IoWarningOutline size={16} className="me-2 flex-shrink-0" />
          <span>
            Pending amount <strong>₹{pendingAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> exists from client. Final Allocation will be enabled once the full project budget is received.
          </span>
        </div>
      )}
    </>
  );

  const renderFooter = () => (
    <>
      <button
        type="button"
        className={isAllocated ? "re-btn-cancel ms-auto" : "re-btn-cancel"}
        onClick={onBackToScope || onHide}
      >
        {isAllocated ? "Close" : "Cancel"}
      </button>
      {!isAllocated && (
        <Tooltip
          title={
            pendingAmount > 0
              ? `Full contract budget has not been received yet (₹${pendingAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pending). Final allocation will be enabled once the full amount is received.`
              : ""
          }
          disableHoverListener={pendingAmount <= 0}
          arrow
          placement="top"
        >
          <span
            style={{
              cursor:
                isAllocatingFixedCost ||
                splitRows.length === 0 ||
                pendingAmount > 0
                  ? "not-allowed"
                  : "default",
              display: "inline-block",
            }}
          >
            <button
              type="button"
              className="re-btn-manual"
              onClick={handleConfirm}
              disabled={
                isAllocatingFixedCost ||
                splitRows.length === 0 ||
                pendingAmount > 0
              }
              style={{
                cursor:
                  isAllocatingFixedCost ||
                  splitRows.length === 0 ||
                  pendingAmount > 0
                    ? "not-allowed"
                    : "pointer",
                pointerEvents:
                  pendingAmount > 0 ? "none" : "auto",
              }}
            >
              {isAllocatingFixedCost
                ? "Allocating..."
                : "Confirm Final Allocation"}
            </button>
          </span>
        </Tooltip>
      )}
    </>
  );

  if (isInline) {
    return (
      <div className={`card shadow-sm border mb-3 ${isDarkMode ? "bg-dark text-white border-secondary" : ""}`} style={{ borderRadius: "8px" }}>
        <div className={`card-header py-3 px-4 border-bottom d-flex align-items-center ${isDarkMode ? "bg-dark text-white border-secondary" : "bg-white"}`}>
          {renderHeader()}
        </div>
        <div className="card-body p-4">
          {renderBody()}
        </div>
        <div className={`card-footer py-3 px-4 d-flex align-items-center justify-content-between border-top ${isDarkMode ? "bg-dark text-white border-secondary" : "bg-light"}`}>
          {renderFooter()}
        </div>
      </div>
    );
  }

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton className={isDarkMode ? "bg-dark text-white border-secondary" : ""}>
        {renderHeader()}
      </Modal.Header>
      <Modal.Body className={`p-4 ${isDarkMode ? "bg-dark text-white" : ""}`}>
        {renderBody()}
      </Modal.Body>
      <Modal.Footer className={`d-flex align-items-center justify-content-between ${isDarkMode ? "bg-dark text-white border-secondary" : "bg-light"}`}>
        {renderFooter()}
      </Modal.Footer>
    </Modal>
  );
};

export default FinalAllocationModal;
